import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Check, AlertTriangle, ChevronRight, ChevronLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function StoryPage() {
  const [steps, setSteps] = useState({});
  const [reflections, setReflections] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [content, setContent] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const stepKeys = Object.keys(steps);
  const currentKey = stepKeys[currentStep] || "";
  const currentInfo = steps[currentKey] || {};
  const progress = stepKeys.length > 0 ? ((currentStep + 1) / stepKeys.length) * 100 : 0;
  const completedCount = Object.keys(reflections).length;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/story/reflections");
        setSteps(res.data.steps || {});
        setReflections(res.data.reflections || {});
        const keys = Object.keys(res.data.steps || {});
        if (keys.length > 0) {
          const existing = res.data.reflections[keys[0]];
          if (existing) setContent(existing.content || "");
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, []);

  useEffect(() => {
    if (stepKeys[currentStep]) {
      const r = reflections[stepKeys[currentStep]];
      setContent(r?.content || "");
      setFeedback(r?.feedback || null);
    }
  }, [currentStep, stepKeys, reflections]);

  const saveReflection = async () => {
    if (!content.trim() || content.trim().length < 20) { toast.error("Bitte schreiben Sie mindestens 20 Zeichen"); return; }
    setSaving(true);
    try {
      const res = await api.post("/story/reflections", { step: currentKey, content });
      setFeedback(res.data.feedback);
      setReflections(prev => ({ ...prev, [currentKey]: { content, feedback: res.data.feedback, has_red_flags: res.data.has_red_flags } }));
      toast.success("Reflexion gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="page-transition max-w-3xl mx-auto space-y-6" data-testid="story-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Ihre Geschichte aufarbeiten</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Bauen Sie Schritt für Schritt eine stimmige, ehrliche Darstellung Ihrer Situation auf</p>
      </div>

      <div className="flex items-center gap-2">
        <Progress value={progress} className="flex-1 h-2" data-testid="story-progress" />
        <span className="font-body text-xs text-muted-foreground">{completedCount}/{stepKeys.length}</span>
      </div>

      {/* Step Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {stepKeys.map((key, i) => {
          const hasReflection = !!reflections[key];
          const hasFlags = reflections[key]?.has_red_flags;
          return (
            <Button key={key} variant={i === currentStep ? "secondary" : "ghost"} size="sm"
              className={`font-body text-xs whitespace-nowrap gap-1 ${hasFlags ? "text-error" : ""}`}
              onClick={() => setCurrentStep(i)} data-testid={`step-nav-${key}`}>
              {hasReflection ? (hasFlags ? <AlertTriangle className="w-3 h-3" /> : <Check className="w-3 h-3 text-success" />) : null}
              {steps[key]?.title}
            </Button>
          );
        })}
      </div>

      {/* Current Step */}
      <Card className="border-border/50" data-testid="reflection-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">{currentInfo.title}</CardTitle>
              <CardDescription className="font-body text-sm">Schritt {currentStep + 1} von {stepKeys.length}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-xl p-4">
            <p className="font-body text-sm text-foreground leading-relaxed">{currentInfo.question}</p>
          </div>

          <Textarea
            value={content} onChange={e => setContent(e.target.value)}
            placeholder="Schreiben Sie Ihre Reflexion hier..."
            className="min-h-[160px] font-body text-sm"
            data-testid="reflection-textarea"
          />

          <Button onClick={saveReflection} disabled={saving || content.trim().length < 20} className="font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="save-reflection-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern & Feedback erhalten
          </Button>

          {/* AI Feedback */}
          {feedback && (
            <Card className={`border-l-4 ${reflections[currentKey]?.has_red_flags ? "border-l-error bg-error/5" : "border-l-success bg-success/5"}`} data-testid="feedback-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  {reflections[currentKey]?.has_red_flags ? <AlertTriangle className="w-4 h-4 text-error" /> : <Check className="w-4 h-4 text-success" />}
                  <p className="font-body text-sm font-medium">{reflections[currentKey]?.has_red_flags ? "Hinweise beachten" : "Feedback"}</p>
                </div>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{feedback}</p>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4">
            <Button variant="ghost" onClick={() => setCurrentStep(s => s - 1)} disabled={currentStep === 0} className="font-body" data-testid="story-prev-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
            <Button variant="ghost" onClick={() => setCurrentStep(s => s + 1)} disabled={currentStep >= stepKeys.length - 1} className="font-body" data-testid="story-next-btn">
              Weiter <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
